import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { get, post, put } from '../utils/api';
import PageLayout from '../components/common/PageLayout';
import MigrationRunLogDialog from '../components/manage/MigrationRunLogDialog';

function variationLabel(v) {
  if (!v) return '';
  const d = (v.display_name || '').trim();
  const s = (v.sku || '').trim();
  if (d && s) return `${d} (${s})`;
  return d || s || `ID ${v.id}`;
}

function formatVariationAmount(priceNum) {
  const n = Number(priceNum);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/** Label for dialog dropdown: name/sku + amount */
function variationSelectLabel(v) {
  const base = variationLabel(v);
  const amt = formatVariationAmount(v?.price_num);
  if (amt) return `${base} — ${amt}`;
  return base;
}

function variationSelectLabelWithId(v) {
  if (!v) return '';
  return `${variationSelectLabel(v)} (ID: ${v.id})`;
}

/** Stable order: lowest price first, then variation id (Woo id order as tie-breaker). */
/** Must match kiosk-backend/lib/civiYearOverrideConstants.js */
const CIVI_YEAR_OVERRIDE_MIN = 2011;
const CIVI_YEAR_OVERRIDE_MAX = 2023;
const YEAR_OVERRIDE_OPTIONS = Array.from(
  { length: CIVI_YEAR_OVERRIDE_MAX - CIVI_YEAR_OVERRIDE_MIN + 1 },
  (_, i) => CIVI_YEAR_OVERRIDE_MIN + i,
);

function sortVariationsForDialog(list) {
  return [...list].sort((a, b) => {
    const pa = Number(a?.price_num);
    const pb = Number(b?.price_num);
    const na = Number.isFinite(pa) ? pa : 0;
    const nb = Number.isFinite(pb) ? pb : 0;
    if (na !== nb) return na - nb;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

function hasScalarId(id) {
  return id != null && String(id).trim() !== '';
}

/** Financial type / contribution page: "Name (ID: 12)" or name only, or "— (ID: 12)" if no name */
function civiCellWithId(name, id) {
  const label = name != null && String(name).trim() !== '' ? String(name).trim() : '';
  const idStr = hasScalarId(id) ? String(id).trim() : '';
  if (label && idStr) return `${label} (ID: ${idStr})`;
  if (label) return label;
  if (idStr) return `— (ID: ${idStr})`;
  return '—';
}

/** Woo product: name from LDB + (ID: n), or — (ID: n) if name missing */
function wooProductCell(r) {
  const idStr = hasScalarId(r.wc_product_id) ? String(r.wc_product_id).trim() : '';
  const name = r.wc_product_name != null && String(r.wc_product_name).trim() !== '' ? String(r.wc_product_name).trim() : '';
  if (!idStr) return '—';
  if (name) return `${name} (ID: ${idStr})`;
  return `— (ID: ${idStr})`;
}

/** Woo variation: label from LDB + (ID: n), or — (ID: n) if label missing */
function wooVariationCell(r) {
  const idStr = hasScalarId(r.wc_variation_id) ? String(r.wc_variation_id).trim() : '';
  const label =
    r.wc_variation_label != null && String(r.wc_variation_label).trim() !== ''
      ? String(r.wc_variation_label).trim()
      : '';
  if (!idStr) return '—';
  if (label) return `${label} (ID: ${idStr})`;
  return `— (ID: ${idStr})`;
}

function fmtMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function mappingFailedCount(r) {
  const n = Number(r?.migrated_failed_count);
  return Number.isFinite(n) ? n : 0;
}

/** For filter Select: completed | in_progress | pending */
function migrationStatusCategory(st) {
  const s = String(st || '').trim().toLowerCase();
  if (s === 'completed' || s === 'success') return 'completed';
  if (!s || s === 'pending') return 'pending';
  return 'in_progress';
}

/** Mapping-level status shown in error color (batch fatal or contribution failures). */
function isMappingRowErrorStatus(st) {
  const s = String(st || '').trim().toLowerCase();
  return s === 'failed' || s === 'mapping_error';
}

function hasAnyMapping(r) {
  const hasDefault = hasScalarId(r?.wc_product_id);
  const raw = r?.amount_override_rules;
  const hasOverrides = Array.isArray(raw)
    ? raw.length > 0
    : (() => {
      if (raw == null || String(raw).trim() === '') return false;
      try {
        const parsed = JSON.parse(String(raw));
        return Array.isArray(parsed) && parsed.length > 0;
      } catch (_) {
        return false;
      }
    })();
  const rawY = r?.year_override_rules;
  const hasYearRules = Array.isArray(rawY)
    ? rawY.length > 0
    : (() => {
      if (rawY == null || String(rawY).trim() === '') return false;
      try {
        const parsed = JSON.parse(String(rawY));
        return Array.isArray(parsed) && parsed.length > 0;
      } catch (_) {
        return false;
      }
    })();
  return hasDefault || hasOverrides || hasYearRules;
}

/** True if dialog form has at least one complete override row or a valid default product (+ variation if required). */
function dialogHasMappableDraft({ selectedProduct, variationId, variations, overrideRules, yearOverrideRules }) {
  const pid = Number(selectedProduct?.id || 0);
  if (Number.isFinite(pid) && pid > 0) {
    if (variations.length > 0 && String(variationId || '').trim() === '') return false;
    return true;
  }
  for (let i = 0; i < overrideRules.length; i += 1) {
    const r = overrideRules[i];
    const min = String(r.min_amount || '').trim();
    const max = String(r.max_amount || '').trim();
    const rid = String(r.wc_product_id || '').trim();
    if (min && max && rid) return true;
  }
  const years = yearOverrideRules || [];
  for (let i = 0; i < years.length; i += 1) {
    const r = years[i];
    const y = String(r.contribution_year || '').trim();
    const rid = String(r.wc_product_id || '').trim();
    if (y && rid) return true;
  }
  return false;
}

function compareMappingRows(a, b, column, dir) {
  let cmp = 0;
  if (column === 'financial_type') {
    cmp = civiCellWithId(a.financial_type_name, a.financial_type_id).localeCompare(
      civiCellWithId(b.financial_type_name, b.financial_type_id),
      undefined,
      { sensitivity: 'base' },
    );
  } else if (column === 'contribution_page') {
    cmp = civiCellWithId(a.contribution_page_name, a.contribution_page_id).localeCompare(
      civiCellWithId(b.contribution_page_name, b.contribution_page_id),
      undefined,
      { sensitivity: 'base' },
    );
  } else if (column === 'woo_product') {
    cmp = wooProductCell(a).localeCompare(wooProductCell(b), undefined, { sensitivity: 'base' });
  } else if (column === 'woo_variation') {
    cmp = wooVariationCell(a).localeCompare(wooVariationCell(b), undefined, { sensitivity: 'base' });
  } else if (column === 'migration_status') {
    cmp = String(a?.migration_status || '').trim().toLowerCase().localeCompare(
      String(b?.migration_status || '').trim().toLowerCase(),
      undefined,
      { sensitivity: 'base' },
    );
  } else if (column === 'mapped') {
    const av = hasAnyMapping(a) ? 1 : 0;
    const bv = hasAnyMapping(b) ? 1 : 0;
    cmp = av - bv;
  }
  return dir === 'desc' ? -cmp : cmp;
}

/** Light-green row: mapping finished with no failed migrations (status completed/success). */
function isMappingRowSuccessHighlight(r) {
  const st = String(r?.migration_status || '').trim().toLowerCase();
  if (st !== 'completed' && st !== 'success') return false;
  return mappingFailedCount(r) === 0;
}

/** Matches Scheduled (queue) filter: server only sets scheduler_queue_min_id for pending/processing rows still eligible (pass_count < max passes). */
function hasActiveSchedulerQueue(r) {
  const q = r?.scheduler_queue_min_id;
  return q != null && Number(q) > 0;
}

/** Run ID written by the scheduler for civi-selected-migration.log / migration-logs API. */
function schedulerQueueJobRunId(row) {
  if (!hasActiveSchedulerQueue(row)) return '';
  return `scheduler-job-${Number(row.scheduler_queue_min_id)}`;
}

function mappingRowFinancialTypeKey(r) {
  return hasScalarId(r?.financial_type_id) ? String(r.financial_type_id).trim() : '__none__';
}

function mappingRowContributionPageKey(r) {
  return hasScalarId(r?.contribution_page_id) ? String(r.contribution_page_id).trim() : '__none__';
}

function formatSchedulerQueueJobMenuLabel(job) {
  if (!job?.id) return '';
  const ftName = job.financial_type_name != null && String(job.financial_type_name).trim() !== ''
    ? String(job.financial_type_name).trim()
    : null;
  const cpName = job.contribution_page_name != null && String(job.contribution_page_name).trim() !== ''
    ? String(job.contribution_page_name).trim()
    : null;
  const ftPart = ftName
    ? `${ftName} (FT ID ${job.financial_type_id})`
    : `FT ID ${job.financial_type_id ?? '—'}`;
  const cpPart = cpName
    ? `${cpName} (page ID ${job.contribution_page_id ?? 0})`
    : `page ID ${job.contribution_page_id ?? '—'}`;
  const st = String(job.status || '—');
  const pc = job.pass_count != null ? String(job.pass_count) : '—';
  const created = job.created_at ? fmtDateTime(job.created_at) : '';
  return `#${job.id} · ${ftPart} · ${cpPart} · ${st} · pass ${pc}${created ? ` · ${created}` : ''}`;
}

function contribDisplayName(c) {
  const first = String(c?.firstName || c?.first_name || '').trim();
  const last = String(c?.lastName || c?.last_name || '').trim();
  const full = `${first} ${last}`.trim();
  return full || '—';
}

function contributionIdOf(c) {
  const raw = c?.contributionId ?? c?.contribution_id ?? c?.id;
  const n = parseInt(String(raw == null ? '' : raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function ManageCiviWcProductMappingPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const schedulerHandledSigRef = React.useRef(null);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [mapOpen, setMapOpen] = React.useState(false);
  const [mappingRow, setMappingRow] = React.useState(null);
  const [productInput, setProductInput] = React.useState('');
  const [productOptions, setProductOptions] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [variations, setVariations] = React.useState([]);
  const [variationId, setVariationId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [overrideRules, setOverrideRules] = React.useState([]);
  const [overrideProductInputs, setOverrideProductInputs] = React.useState({});
  const [overrideProductOptions, setOverrideProductOptions] = React.useState({});
  const [overrideVariationOptions, setOverrideVariationOptions] = React.useState({});
  const [yearOverrideRules, setYearOverrideRules] = React.useState([]);
  const [yearOverrideProductInputs, setYearOverrideProductInputs] = React.useState({});
  const [yearOverrideProductOptions, setYearOverrideProductOptions] = React.useState({});
  const [yearOverrideVariationOptions, setYearOverrideVariationOptions] = React.useState({});
  const [searchQ, setSearchQ] = React.useState('');
  const [filterMigrationStatus, setFilterMigrationStatus] = React.useState('all');
  const [filterWooProduct, setFilterWooProduct] = React.useState('');
  const [filterFinancialType, setFilterFinancialType] = React.useState('');
  const [filterContributionPage, setFilterContributionPage] = React.useState('');
  const [sortBy, setSortBy] = React.useState('');
  const [sortDir, setSortDir] = React.useState('asc');
  const [rowActionsAnchor, setRowActionsAnchor] = React.useState(null);
  const [rowActionsRow, setRowActionsRow] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsRow, setDetailsRow] = React.useState(null);
  const [contribOpen, setContribOpen] = React.useState(false);
  const [contribRow, setContribRow] = React.useState(null);
  const [contribLoading, setContribLoading] = React.useState(false);
  const [contribError, setContribError] = React.useState('');
  const [contribData, setContribData] = React.useState([]);
  const [contribPage, setContribPage] = React.useState(1);
  const [contribPageSize] = React.useState(25);
  const [contribTotalRecords, setContribTotalRecords] = React.useState(0);
  const [contribTotalPages, setContribTotalPages] = React.useState(0);
  const [contribSelectedIds, setContribSelectedIds] = React.useState([]);
  const [contribMarkCompletedMsg, setContribMarkCompletedMsg] = React.useState('');
  const [contribMarking, setContribMarking] = React.useState(false);
  const [recordMigOpen, setRecordMigOpen] = React.useState(false);
  const [recordMigRows, setRecordMigRows] = React.useState([]);
  const [recordMigProductInput, setRecordMigProductInput] = React.useState('');
  const [recordMigProductOptions, setRecordMigProductOptions] = React.useState([]);
  const [recordMigSelectedProduct, setRecordMigSelectedProduct] = React.useState(null);
  const [recordMigVariations, setRecordMigVariations] = React.useState([]);
  const [recordMigVariationId, setRecordMigVariationId] = React.useState('');
  const [recordMigSubmitting, setRecordMigSubmitting] = React.useState(false);
  const [recordMigError, setRecordMigError] = React.useState('');
  const [recordMigNotice, setRecordMigNotice] = React.useState('');
  const [recordMigResult, setRecordMigResult] = React.useState(null);
  const [recordMigRunId, setRecordMigRunId] = React.useState('');
  const [recordMigRunState, setRecordMigRunState] = React.useState('');
  const [recordMigSummary, setRecordMigSummary] = React.useState(null);
  const [recordMigStatusById, setRecordMigStatusById] = React.useState({});
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [logsQ, setLogsQ] = React.useState('');
  const [logsRunId, setLogsRunId] = React.useState('');
  const [logsLevel, setLogsLevel] = React.useState('all');
  const [logsLimit, setLogsLimit] = React.useState('200');
  const [logsAutoRefresh, setLogsAutoRefresh] = React.useState(true);
  const [logsLoading, setLogsLoading] = React.useState(false);
  const [logsError, setLogsError] = React.useState('');
  const [logsTotal, setLogsTotal] = React.useState(0);
  const [logsRows, setLogsRows] = React.useState([]);
  const [logsSelFinancialType, setLogsSelFinancialType] = React.useState('');
  const [logsSelContributionPage, setLogsSelContributionPage] = React.useState('');
  const [logsQueueJobId, setLogsQueueJobId] = React.useState('');
  const [logsQueueJobs, setLogsQueueJobs] = React.useState([]);
  const [logsQueueLoading, setLogsQueueLoading] = React.useState(false);
  const [inlineRunLogOpen, setInlineRunLogOpen] = React.useState(false);
  const [inlineRunLogId, setInlineRunLogId] = React.useState('');
  const [inlineRunLogQ, setInlineRunLogQ] = React.useState('');

  const [migOpen, setMigOpen] = React.useState(false);
  const [migRow, setMigRow] = React.useState(null);
  const [migOutcome, setMigOutcome] = React.useState('completed');
  const [migLoading, setMigLoading] = React.useState(false);
  const [migError, setMigError] = React.useState('');
  const [migData, setMigData] = React.useState([]);
  const [migPage, setMigPage] = React.useState(1);
  const [migPageSize] = React.useState(25);
  const [migTotalRecords, setMigTotalRecords] = React.useState(0);
  const [migTotalPages, setMigTotalPages] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/civi-wc-product-mapping');
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load mapping rows');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!productInput || productInput.length < 2) {
      setProductOptions([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: productInput, limit: 80 });
        if (!ignore) setProductOptions(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setProductOptions([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [productInput]);

  React.useEffect(() => {
    if (!selectedProduct?.id) {
      setVariations([]);
      setVariationId('');
      return;
    }
    const mapPid = mappingRow?.wc_product_id != null ? String(mappingRow.wc_product_id).trim() : '';
    const sameProduct = mapPid !== '' && String(selectedProduct.id) === mapPid;
    const preserveVid =
      sameProduct && hasScalarId(mappingRow?.wc_variation_id)
        ? String(mappingRow.wc_variation_id).trim()
        : '';
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(selectedProduct.id), limit: 300 });
        const list = Array.isArray(res?.variations) ? res.variations : [];
        if (!ignore) {
          setVariations(sortVariationsForDialog(list));
          const match = preserveVid && list.some((x) => String(x.id) === preserveVid);
          setVariationId(match ? preserveVid : '');
        }
      } catch {
        if (!ignore) {
          setVariations([]);
          setVariationId('');
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [selectedProduct, mappingRow?.id, mappingRow?.wc_product_id, mappingRow?.wc_variation_id]);

  React.useEffect(() => {
    if (!recordMigProductInput || recordMigProductInput.length < 2) {
      setRecordMigProductOptions([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: recordMigProductInput, limit: 80 });
        if (!ignore) setRecordMigProductOptions(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setRecordMigProductOptions([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [recordMigProductInput]);

  React.useEffect(() => {
    if (!recordMigSelectedProduct?.id) {
      setRecordMigVariations([]);
      setRecordMigVariationId('');
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(recordMigSelectedProduct.id), limit: 300 });
        const list = Array.isArray(res?.variations) ? sortVariationsForDialog(res.variations) : [];
        if (!ignore) {
          setRecordMigVariations(list);
          if (!list.some((v) => String(v.id) === String(recordMigVariationId))) setRecordMigVariationId('');
        }
      } catch {
        if (!ignore) {
          setRecordMigVariations([]);
          setRecordMigVariationId('');
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [recordMigSelectedProduct?.id]);

  const openMap = (row) => {
    setMappingRow(row);
    setError('');
    setOk('');
    setProductOptions([]);
    setVariations([]);
    setVariationId('');
    const rawRules = Array.isArray(row?.amount_override_rules) ? row.amount_override_rules : [];
    const mappedRules = rawRules.map((r) => ({
      min_amount: r?.min_amount != null ? String(r.min_amount) : '',
      max_amount: r?.max_amount != null ? String(r.max_amount) : '',
      wc_product_id: r?.wc_product_id != null ? String(r.wc_product_id) : '',
      wc_product_name: r?.wc_product_name != null ? String(r.wc_product_name) : '',
      wc_variation_id: r?.wc_variation_id != null ? String(r.wc_variation_id) : '',
    }));
    setOverrideRules(mappedRules);
    const initInputs = {};
    mappedRules.forEach((r, idx) => {
      const pid = String(r.wc_product_id || '').trim();
      const name = String(r.wc_product_name || '').trim();
      if (pid && name) initInputs[idx] = `${name} (#${pid})`;
    });
    setOverrideProductInputs(initInputs);
    setOverrideProductOptions({});
    setOverrideVariationOptions({});
    const rawYear = Array.isArray(row?.year_override_rules) ? row.year_override_rules : [];
    const mappedYear = rawYear.map((r) => ({
      contribution_year: r?.contribution_year != null ? String(r.contribution_year) : '',
      wc_product_id: r?.wc_product_id != null ? String(r.wc_product_id) : '',
      wc_product_name: r?.wc_product_name != null ? String(r.wc_product_name) : '',
      wc_variation_id: r?.wc_variation_id != null ? String(r.wc_variation_id) : '',
    }));
    setYearOverrideRules(mappedYear);
    const yInitInputs = {};
    mappedYear.forEach((r, idx) => {
      const p = String(r.wc_product_id || '').trim();
      const name = String(r.wc_product_name || '').trim();
      if (p && name) yInitInputs[idx] = `${name} (#${p})`;
    });
    setYearOverrideProductInputs(yInitInputs);
    setYearOverrideProductOptions({});
    setYearOverrideVariationOptions({});
    const pidRaw = row?.wc_product_id;
    if (hasScalarId(pidRaw)) {
      const pid = parseInt(String(pidRaw).trim(), 10);
      if (Number.isFinite(pid) && pid > 0) {
        const name =
          row.wc_product_name != null && String(row.wc_product_name).trim() !== ''
            ? String(row.wc_product_name).trim()
            : '';
        setSelectedProduct({ id: pid, name });
        setProductInput(name ? `${name} (#${pid})` : String(pid));
      } else {
        setSelectedProduct(null);
        setProductInput('');
      }
    } else {
      setSelectedProduct(null);
      setProductInput('');
    }
    setMapOpen(true);
  };

  const closeMap = () => {
    setMapOpen(false);
    setMappingRow(null);
    setOverrideRules([]);
    setOverrideProductInputs({});
    setOverrideProductOptions({});
    setOverrideVariationOptions({});
    setYearOverrideRules([]);
    setYearOverrideProductInputs({});
    setYearOverrideProductOptions({});
    setYearOverrideVariationOptions({});
  };

  const addOverrideRuleRow = () => {
    setOverrideRules((prev) => [...prev, { min_amount: '', max_amount: '', wc_product_id: '', wc_product_name: '', wc_variation_id: '' }]);
  };

  const removeOverrideRuleRow = (idx) => {
    setOverrideRules((prev) => prev.filter((_, i) => i !== idx));
    setOverrideProductInputs({});
    setOverrideProductOptions({});
    setOverrideVariationOptions({});
  };

  const setOverrideRuleField = (idx, key, value) => {
    setOverrideRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const loadOverrideProductOptions = React.useCallback(async (idx, q) => {
    const term = String(q || '').trim();
    if (term.length < 2) {
      setOverrideProductOptions((prev) => ({ ...prev, [idx]: [] }));
      return;
    }
    try {
      const res = await get('/ldb/facets/products', { q: term, limit: 80 });
      const list = Array.isArray(res?.products) ? res.products : [];
      setOverrideProductOptions((prev) => ({ ...prev, [idx]: list }));
    } catch {
      setOverrideProductOptions((prev) => ({ ...prev, [idx]: [] }));
    }
  }, []);

  const loadOverrideVariations = React.useCallback(async (idx, productId, preserveVariationId = '') => {
    const pid = Number(productId || 0);
    if (!pid) {
      setOverrideVariationOptions((prev) => ({ ...prev, [idx]: [] }));
      return;
    }
    try {
      const res = await get('/ldb/facets/variations', { product_id: String(pid), limit: 300 });
      const list = Array.isArray(res?.variations) ? sortVariationsForDialog(res.variations) : [];
      setOverrideVariationOptions((prev) => ({ ...prev, [idx]: list }));
      if (!preserveVariationId) {
        setOverrideRuleField(idx, 'wc_variation_id', '');
        return;
      }
      const match = list.some((v) => String(v.id) === String(preserveVariationId));
      if (!match) setOverrideRuleField(idx, 'wc_variation_id', '');
    } catch {
      setOverrideVariationOptions((prev) => ({ ...prev, [idx]: [] }));
      if (!preserveVariationId) setOverrideRuleField(idx, 'wc_variation_id', '');
    }
  }, []);

  const addYearOverrideRuleRow = () => {
    setYearOverrideRules((prev) => [
      ...prev,
      { contribution_year: '', wc_product_id: '', wc_product_name: '', wc_variation_id: '' },
    ]);
  };

  const removeYearOverrideRuleRow = (idx) => {
    setYearOverrideRules((prev) => prev.filter((_, i) => i !== idx));
    setYearOverrideProductInputs({});
    setYearOverrideProductOptions({});
    setYearOverrideVariationOptions({});
  };

  const setYearOverrideRuleField = (idx, key, value) => {
    setYearOverrideRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const loadYearOverrideProductOptions = React.useCallback(async (idx, q) => {
    const term = String(q || '').trim();
    if (term.length < 2) {
      setYearOverrideProductOptions((prev) => ({ ...prev, [idx]: [] }));
      return;
    }
    try {
      const res = await get('/ldb/facets/products', { q: term, limit: 80 });
      const list = Array.isArray(res?.products) ? res.products : [];
      setYearOverrideProductOptions((prev) => ({ ...prev, [idx]: list }));
    } catch {
      setYearOverrideProductOptions((prev) => ({ ...prev, [idx]: [] }));
    }
  }, []);

  const loadYearOverrideVariations = React.useCallback(async (idx, productId, preserveVariationId = '') => {
    const pid = Number(productId || 0);
    if (!pid) {
      setYearOverrideVariationOptions((prev) => ({ ...prev, [idx]: [] }));
      return;
    }
    try {
      const res = await get('/ldb/facets/variations', { product_id: String(pid), limit: 300 });
      const list = Array.isArray(res?.variations) ? sortVariationsForDialog(res.variations) : [];
      setYearOverrideVariationOptions((prev) => ({ ...prev, [idx]: list }));
      if (!preserveVariationId) {
        setYearOverrideRuleField(idx, 'wc_variation_id', '');
        return;
      }
      const match = list.some((v) => String(v.id) === String(preserveVariationId));
      if (!match) setYearOverrideRuleField(idx, 'wc_variation_id', '');
    } catch {
      setYearOverrideVariationOptions((prev) => ({ ...prev, [idx]: [] }));
      if (!preserveVariationId) setYearOverrideRuleField(idx, 'wc_variation_id', '');
    }
  }, []);

  React.useEffect(() => {
    if (!mapOpen) return;
    overrideRules.forEach((r, idx) => {
      const pid = String(r?.wc_product_id || '').trim();
      if (!pid) return;
      if (overrideVariationOptions[idx]) return;
      loadOverrideVariations(idx, pid, String(r?.wc_variation_id || '').trim());
    });
  }, [mapOpen, overrideRules, overrideVariationOptions, loadOverrideVariations]);

  React.useEffect(() => {
    if (!mapOpen) return;
    yearOverrideRules.forEach((r, idx) => {
      const pid = String(r?.wc_product_id || '').trim();
      if (!pid) return;
      if (yearOverrideVariationOptions[idx]) return;
      loadYearOverrideVariations(idx, pid, String(r?.wc_variation_id || '').trim());
    });
  }, [mapOpen, yearOverrideRules, yearOverrideVariationOptions, loadYearOverrideVariations]);

  React.useEffect(() => {
    if (!mapOpen || !mappingRow?.id) return undefined;
    const rules = Array.isArray(mappingRow.amount_override_rules) ? mappingRow.amount_override_rules : [];
    let ignore = false;
    (async () => {
      const updates = [];
      for (let idx = 0; idx < rules.length; idx += 1) {
        const r = rules[idx];
        const pid = String(r?.wc_product_id ?? '').trim();
        if (!/^[0-9]+$/.test(pid)) continue;
        const hasName = r?.wc_product_name != null && String(r.wc_product_name).trim() !== '';
        if (hasName) continue;
        try {
          const res = await get('/ldb/facets/products', { product_id: pid });
          if (ignore) return;
          const p = Array.isArray(res?.products) ? res.products[0] : null;
          const nm = p?.name != null ? String(p.name).trim() : '';
          if (nm) updates.push({ idx, name: nm, pid });
        } catch {
          // best-effort
        }
      }
      if (ignore || !updates.length) return;
      setOverrideRules((prev) => {
        const next = [...prev];
        updates.forEach(({ idx, name }) => {
          if (next[idx]) next[idx] = { ...next[idx], wc_product_name: name };
        });
        return next;
      });
      setOverrideProductInputs((prev) => {
        const next = { ...prev };
        updates.forEach(({ idx, name, pid }) => {
          next[idx] = `${name} (#${pid})`;
        });
        return next;
      });
    })();
    return () => {
      ignore = true;
    };
  }, [mapOpen, mappingRow?.id, mappingRow?.amount_override_rules]);

  React.useEffect(() => {
    if (!mapOpen || !mappingRow?.id) return undefined;
    const rules = Array.isArray(mappingRow.year_override_rules) ? mappingRow.year_override_rules : [];
    let ignore = false;
    (async () => {
      const updates = [];
      for (let idx = 0; idx < rules.length; idx += 1) {
        const r = rules[idx];
        const pid = String(r?.wc_product_id ?? '').trim();
        if (!/^[0-9]+$/.test(pid)) continue;
        const hasName = r?.wc_product_name != null && String(r.wc_product_name).trim() !== '';
        if (hasName) continue;
        try {
          const res = await get('/ldb/facets/products', { product_id: pid });
          if (ignore) return;
          const p = Array.isArray(res?.products) ? res.products[0] : null;
          const nm = p?.name != null ? String(p.name).trim() : '';
          if (nm) updates.push({ idx, name: nm, pid });
        } catch {
          // best-effort
        }
      }
      if (ignore || !updates.length) return;
      setYearOverrideRules((prev) => {
        const next = [...prev];
        updates.forEach(({ idx, name }) => {
          if (next[idx]) next[idx] = { ...next[idx], wc_product_name: name };
        });
        return next;
      });
      setYearOverrideProductInputs((prev) => {
        const next = { ...prev };
        updates.forEach(({ idx, name, pid }) => {
          next[idx] = `${name} (#${pid})`;
        });
        return next;
      });
    })();
    return () => {
      ignore = true;
    };
  }, [mapOpen, mappingRow?.id, mappingRow?.year_override_rules]);

  const requestSort = (column) => {
    const isAsc = sortBy === column && sortDir === 'asc';
    setSortDir(isAsc ? 'desc' : 'asc');
    setSortBy(column);
  };

  const persistMapping = async (andSchedule) => {
    if (!mappingRow?.id) return;
    const st = String(mappingRow?.migration_status || '').trim().toLowerCase();
    if (andSchedule && (st === 'completed' || st === 'success')) {
      setError('Cannot schedule: migration status is already completed.');
      return;
    }
    if (
      andSchedule
      && !dialogHasMappableDraft({
        selectedProduct,
        variationId,
        variations,
        overrideRules,
        yearOverrideRules,
      })
    ) {
      setError('Add at least one amount override, year rule, or default Woo product before scheduling.');
      return;
    }
    const pid = Number(selectedProduct?.id || 0);
    if (Number.isFinite(pid) && pid > 0 && variations.length > 0 && variationId === '') {
      setError('This product has variations — pick one. Saving without a variation breaks Civi→Woo migration for variable products.');
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      const body = {};
      if (Number.isFinite(pid) && pid > 0) {
        body.wc_product_id = String(pid);
        const vid = variationId === '' ? '' : String(variationId);
        if (vid !== '') body.wc_variation_id = vid;
      } else {
        body.wc_product_id = null;
      }
      const parsedRules = [];
      for (let i = 0; i < overrideRules.length; i += 1) {
        const r = overrideRules[i];
        const min = String(r.min_amount || '').trim();
        const max = String(r.max_amount || '').trim();
        const rid = String(r.wc_product_id || '').trim();
        const rvid = String(r.wc_variation_id || '').trim();
        if (!min && !max && !rid && !rvid) continue;
        if (!min || !max || !rid) {
          setError(`Override row ${i + 1}: min, max, and product id are required.`);
          setSaving(false);
          return;
        }
        if (Number(min) > Number(max)) {
          setError(`Override row ${i + 1}: min amount cannot be greater than max amount.`);
          setSaving(false);
          return;
        }
        const nameTrim = String(r.wc_product_name || '').trim();
        parsedRules.push({
          min_amount: Number(min),
          max_amount: Number(max),
          wc_product_id: Number(rid),
          ...(nameTrim ? { wc_product_name: nameTrim } : {}),
          ...(rvid ? { wc_variation_id: Number(rvid) } : {}),
        });
      }
      body.amount_override_rules = parsedRules;
      const parsedYearRules = [];
      for (let i = 0; i < yearOverrideRules.length; i += 1) {
        const r = yearOverrideRules[i];
        const yStr = String(r.contribution_year || '').trim();
        const rid = String(r.wc_product_id || '').trim();
        const rvid = String(r.wc_variation_id || '').trim();
        if (!yStr && !rid && !rvid) continue;
        if (!yStr || !rid) {
          setError(`Year rule row ${i + 1}: choose a year (${CIVI_YEAR_OVERRIDE_MIN}–${CIVI_YEAR_OVERRIDE_MAX}) and a product.`);
          setSaving(false);
          return;
        }
        const yNum = Number(yStr);
        if (
          !Number.isFinite(yNum)
          || Math.floor(yNum) !== yNum
          || yNum < CIVI_YEAR_OVERRIDE_MIN
          || yNum > CIVI_YEAR_OVERRIDE_MAX
        ) {
          setError(`Year rule row ${i + 1}: year must be ${CIVI_YEAR_OVERRIDE_MIN}–${CIVI_YEAR_OVERRIDE_MAX}.`);
          setSaving(false);
          return;
        }
        const nameTrim = String(r.wc_product_name || '').trim();
        parsedYearRules.push({
          contribution_year: yNum,
          wc_product_id: Number(rid),
          ...(nameTrim ? { wc_product_name: nameTrim } : {}),
          ...(rvid ? { wc_variation_id: Number(rvid) } : {}),
        });
      }
      body.year_override_rules = parsedYearRules;
      const res = await put(`/api/manage/civi-wc-product-mapping/${mappingRow.id}`, body);
      const updated = res?.row;
      if (updated) {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setMappingRow((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      } else {
        await load();
      }
      if (andSchedule) {
        await post(`/api/manage/civi-wc-product-mapping/${mappingRow.id}/schedule-migration-queue`, {});
        setOk('Mapping saved and added to the migration queue (FIFO, one pass per item; 5 min pause after each item completes or fails before the next).');
      } else {
        setOk('Mapping saved.');
      }
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const onSaveMapping = () => persistMapping(false);
  const onMapAndSchedule = () => persistMapping(true);

  const productAutocompleteOptions = React.useMemo(() => {
    if (!selectedProduct?.id) return productOptions;
    const has = productOptions.some((o) => Number(o?.id) === Number(selectedProduct.id));
    if (has) return productOptions;
    return [selectedProduct, ...productOptions];
  }, [productOptions, selectedProduct]);

  const wooProductFilterOptions = React.useMemo(() => {
    const hasNone = rows.some((r) => !hasScalarId(r.wc_product_id));
    const ids = new Set();
    rows.forEach((r) => {
      if (hasScalarId(r.wc_product_id)) ids.add(String(r.wc_product_id).trim());
    });
    const opts = [];
    if (hasNone) opts.push({ value: '__none__', label: '— (no Woo product)' });
    [...ids].sort((a, b) => Number(a) - Number(b)).forEach((id) => {
      const sample = rows.find((rr) => String(rr.wc_product_id || '').trim() === id);
      opts.push({ value: id, label: sample ? wooProductCell(sample) : `ID ${id}` });
    });
    return opts;
  }, [rows]);

  const financialTypeFilterOptions = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = hasScalarId(r.financial_type_id) ? String(r.financial_type_id).trim() : '__none__';
      m.set(key, civiCellWithId(r.financial_type_name, r.financial_type_id));
    });
    return [...m.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const contributionPageFilterOptions = React.useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const key = hasScalarId(r.contribution_page_id) ? String(r.contribution_page_id).trim() : '__none__';
      m.set(key, civiCellWithId(r.contribution_page_name, r.contribution_page_id));
    });
    return [...m.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const logsCpOptionsForDialog = React.useMemo(() => {
    const ft = String(logsSelFinancialType || '').trim();
    const source =
      ft === ''
        ? rows
        : rows.filter((r) => mappingRowFinancialTypeKey(r) === ft);
    const m = new Map();
    source.forEach((r) => {
      const key = mappingRowContributionPageKey(r);
      m.set(key, civiCellWithId(r.contribution_page_name, r.contribution_page_id));
    });
    return [...m.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows, logsSelFinancialType]);

  const filteredRows = React.useMemo(() => {
    let list = rows;
    const q = String(searchQ || '').trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        String(r.financial_type_name || '').toLowerCase().includes(q)
        || String(r.contribution_page_name || '').toLowerCase().includes(q)
        || String(r.wc_product_name || '').toLowerCase().includes(q)
        || String(r.migration_status || '').toLowerCase().includes(q)
        || String(r.error_message || '').toLowerCase().includes(q)
      );
    }
    if (filterMigrationStatus === 'scheduled') {
      list = list.filter((r) => {
        const q = r?.scheduler_queue_min_id;
        return q != null && Number(q) > 0;
      });
    } else if (filterMigrationStatus !== 'all') {
      list = list.filter((r) => migrationStatusCategory(r.migration_status) === filterMigrationStatus);
    }
    if (filterWooProduct !== '') {
      list = list.filter((r) => {
        const id = hasScalarId(r.wc_product_id) ? String(r.wc_product_id).trim() : '';
        if (filterWooProduct === '__none__') return id === '';
        return id === filterWooProduct;
      });
    }
    if (filterFinancialType !== '') {
      list = list.filter((r) => {
        const key = hasScalarId(r.financial_type_id) ? String(r.financial_type_id).trim() : '__none__';
        return key === filterFinancialType;
      });
    }
    if (filterContributionPage !== '') {
      list = list.filter((r) => {
        const key = hasScalarId(r.contribution_page_id) ? String(r.contribution_page_id).trim() : '__none__';
        return key === filterContributionPage;
      });
    }
    if (filterMigrationStatus === 'scheduled') {
      list = [...list].sort((a, b) => {
        const da = Number(a.scheduler_queue_min_id);
        const db = Number(b.scheduler_queue_min_id);
        if (da !== db) return da - db;
        return Number(a.id) - Number(b.id);
      });
    } else if (sortBy) {
      list = [...list].sort((a, b) => compareMappingRows(a, b, sortBy, sortDir));
    }
    return list;
  }, [
    rows,
    searchQ,
    filterMigrationStatus,
    filterWooProduct,
    filterFinancialType,
    filterContributionPage,
    sortBy,
    sortDir,
  ]);

  const openDetails = (row) => {
    setDetailsRow(row || null);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsRow(null);
  };

  const loadContributions = React.useCallback(async (row, page) => {
    if (!row) return;
    const financialTypeId = Number(row.financial_type_id);
    const rawContributionPageId = Number(row.contribution_page_id);
    const contributionPageId = Number.isFinite(rawContributionPageId) && rawContributionPageId >= 0 ? rawContributionPageId : 0;
    if (!Number.isFinite(financialTypeId)) {
      setContribError('Invalid financial type or contribution page id for this row.');
      setContribData([]);
      setContribTotalPages(0);
      setContribTotalRecords(0);
      return;
    }
    setContribLoading(true);
    setContribError('');
    try {
      const res = await get('/api/manage/civi-wc-product-mapping/contributions', {
        financialTypeId,
        contributionPageId,
        page,
        pageSize: contribPageSize,
      });
      const payload = res || {};
      setContribData(Array.isArray(payload.data) ? payload.data : []);
      setContribPage(Number(payload.page || page || 1));
      setContribTotalRecords(Number(payload.totalRecords || 0));
      setContribTotalPages(Number(payload.totalPages || 0));
    } catch (e) {
      setContribData([]);
      setContribTotalRecords(0);
      setContribTotalPages(0);
      setContribError(e?.data?.error || e?.message || 'Failed to fetch contribution records');
    } finally {
      setContribLoading(false);
    }
  }, [contribPageSize]);

  const openContributions = (row) => {
    setContribRow(row || null);
    setContribData([]);
    setContribError('');
    setContribMarkCompletedMsg('');
    setContribPage(1);
    setContribTotalPages(0);
    setContribTotalRecords(0);
    setRecordMigRunId('');
    setRecordMigRunState('');
    setRecordMigSummary(null);
    setRecordMigStatusById({});
    setContribOpen(true);
    loadContributions(row, 1);
  };

  const closeContributions = () => {
    setContribOpen(false);
    setContribRow(null);
    setContribData([]);
    setContribError('');
    setContribMarkCompletedMsg('');
    setContribPage(1);
    setContribTotalPages(0);
    setContribTotalRecords(0);
    setContribSelectedIds([]);
    setRecordMigRunId('');
    setRecordMigRunState('');
    setRecordMigSummary(null);
    setRecordMigStatusById({});
  };

  const onMarkMigrationCompleted = React.useCallback(async () => {
    if (!contribRow?.id) return;
    setContribMarking(true);
    setContribMarkCompletedMsg('');
    setContribError('');
    try {
      const res = await post(`/api/manage/civi-wc-product-mapping/${contribRow.id}/mark-migration-completed`, {});
      const updated = res?.row;
      if (updated) {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setContribRow((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      }
      setContribMarkCompletedMsg('Migration marked as completed for this mapping row.');
    } catch (e) {
      setContribError(e?.data?.error || e?.message || 'Failed to mark migration completed');
    } finally {
      setContribMarking(false);
    }
  }, [contribRow?.id]);

  const contributionSelectionOnPage = React.useMemo(() => {
    const ids = contribData.map(contributionIdOf).filter((n) => Number.isFinite(n));
    const selected = ids.filter((id) => contribSelectedIds.includes(id));
    return {
      pageIds: ids,
      selectedCount: selected.length,
      allChecked: ids.length > 0 && selected.length === ids.length,
      indeterminate: selected.length > 0 && selected.length < ids.length,
    };
  }, [contribData, contribSelectedIds]);

  const toggleSelectAllOnPage = (checked) => {
    const pageIds = contributionSelectionOnPage.pageIds;
    if (!pageIds.length) return;
    if (checked) {
      setContribSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      return;
    }
    setContribSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
  };

  const toggleContributionSelection = (contributionId, checked) => {
    if (!Number.isFinite(contributionId)) return;
    if (checked) {
      setContribSelectedIds((prev) => (prev.includes(contributionId) ? prev : [...prev, contributionId]));
      return;
    }
    setContribSelectedIds((prev) => prev.filter((id) => id !== contributionId));
  };

  const contributionRunStatus = React.useCallback((contributionId) => {
    if (!Number.isFinite(contributionId)) return '';
    return String(recordMigStatusById[contributionId] || '').trim().toLowerCase();
  }, [recordMigStatusById]);

  const openRecordMigrationDialog = (rowsForMigration) => {
    const list = Array.isArray(rowsForMigration) ? rowsForMigration.filter(Boolean) : [];
    if (!list.length) return;
    setRecordMigRows(list);
    setRecordMigOpen(true);
    setRecordMigError('');
    setRecordMigResult(null);
    setRecordMigSubmitting(false);
    setRecordMigSelectedProduct(null);
    setRecordMigProductInput('');
    setRecordMigProductOptions([]);
    setRecordMigVariations([]);
    setRecordMigVariationId('');
    setRecordMigRunId('');
    setRecordMigRunState('');
    setRecordMigSummary(null);
    setRecordMigStatusById({});
  };

  const openSingleRecordMigration = (row) => {
    openRecordMigrationDialog([row]);
  };

  const openGroupMigration = () => {
    const byId = new Set(contribSelectedIds);
    const list = contribData.filter((r) => byId.has(contributionIdOf(r)));
    openRecordMigrationDialog(list);
  };

  const closeRecordMigrationDialog = () => {
    setRecordMigOpen(false);
    setRecordMigRows([]);
    setRecordMigError('');
    setRecordMigNotice('');
    setRecordMigSubmitting(false);
    setRecordMigRunId('');
    setRecordMigRunState('');
    setRecordMigSummary(null);
  };

  /** When POST/GET migrate-selected fails or run is missing on this Node instance, load outcomes from Postgres. */
  const reconcileRecordMigrationFromDb = React.useCallback(
    async (contributionIds, noticeText) => {
      if (!contribRow?.id || !Array.isArray(contributionIds) || contributionIds.length === 0) return false;
      try {
        const rec = await get(
          `/api/manage/civi-wc-product-mapping/${encodeURIComponent(String(contribRow.id))}/migrated-contributions-by-ids`,
          { ids: contributionIds.join(',') },
        );
        const rows = Array.isArray(rec?.data) ? rec.data : [];
        if (rows.length === 0) return false;
        const byId = Object.fromEntries(rows.map((r) => [Number(r.contribution_id), r]));
        const statusMap = {};
        let completed = 0;
        let failed = 0;
        for (const id of contributionIds) {
          const row = byId[id];
          if (!row) {
            statusMap[id] = 'failed';
            failed += 1;
            continue;
          }
          const ok =
            String(row?.migration_status || '').trim().toLowerCase() === 'completed'
            && String(row?.civi_cancel_status || '').trim().toLowerCase() === 'success'
            && row?.wc_order_id != null;
          statusMap[id] = ok ? 'completed' : 'failed';
          if (ok) completed += 1;
          else failed += 1;
        }
        setRecordMigStatusById((prev) => ({ ...prev, ...statusMap }));
        setRecordMigSummary({ queued: 0, processing: 0, completed, failed });
        setRecordMigResult({ data: rows });
        setRecordMigError('');
        setRecordMigRunState(failed === contributionIds.length ? 'failed' : 'completed');
        setRecordMigNotice(noticeText);
        return true;
      } catch {
        return false;
      }
    },
    [contribRow?.id],
  );

  const onProceedRecordMigration = async () => {
    if (!contribRow) return;
    const productId = Number(recordMigSelectedProduct?.id || 0);
    if (!productId) {
      setRecordMigError('Select a WooCommerce product.');
      return;
    }
    if (recordMigVariations.length > 0 && recordMigVariationId === '') {
      setRecordMigError('This product has variations — pick one.');
      return;
    }
    const contributionIds = recordMigRows.map(contributionIdOf).filter((n) => Number.isFinite(n));
    if (!contributionIds.length) {
      setRecordMigError('No valid contribution IDs selected.');
      return;
    }

    setRecordMigSubmitting(true);
    setRecordMigError('');
    setRecordMigNotice('');
    try {
      const contributionPageId = (() => {
        const n = Number(contribRow.contribution_page_id);
        return Number.isFinite(n) && n >= 0 ? n : 0;
      })();
      const baseBody = {
        financialTypeId: Number(contribRow.financial_type_id),
        contributionPageId,
        wc_product_id: productId,
      };
      if (recordMigVariationId !== '') baseBody.wc_variation_id = Number(recordMigVariationId);

      setRecordMigRunId('');
      setRecordMigRunState('running');
      setRecordMigResult(null);
      setRecordMigStatusById(Object.fromEntries(contributionIds.map((id) => [id, 'processing'])));
      setRecordMigSummary({
        queued: 0,
        processing: contributionIds.length,
        completed: 0,
        failed: 0,
      });

      const startRes = await post(
        '/api/manage/civi-wc-product-mapping/migrate-selected',
        { ...baseBody, contribution_ids: contributionIds },
        { timeout: 0 },
      );
      const startedRunId = String(startRes?.runId || '').trim();
      if (!startedRunId) {
        throw new Error('Migration run did not return runId');
      }
      setRecordMigRunId(startedRunId);
      setRecordMigRunState(String(startRes?.state || 'running'));
      setRecordMigNotice(`Migration started (run ${startedRunId}). Status updates will refresh automatically.`);
    } catch (e) {
      setRecordMigRunState('failed');
      const reqErr = e?.data?.error || e?.message || 'Failed to migrate selected contribution records';
      const notice = `Browser request failed, but current statuses were loaded from the database (completed / failed counts below).`;
      const reconciled = await reconcileRecordMigrationFromDb(contributionIds, notice);
      if (!reconciled) {
        setRecordMigError(reqErr);
        setRecordMigSummary({ queued: 0, processing: 0, completed: 0, failed: contributionIds.length });
        setRecordMigStatusById(Object.fromEntries(contributionIds.map((id) => [id, 'failed'])));
      }
    } finally {
      setRecordMigSubmitting(false);
    }
  };

  const loadMigratedDialog = React.useCallback(async (row, outcome, page) => {
    if (!row?.id) return;
    setMigLoading(true);
    setMigError('');
    try {
      const res = await get(`/api/manage/civi-wc-product-mapping/${encodeURIComponent(String(row.id))}/migrated-contributions`, {
        outcome,
        page,
        pageSize: migPageSize,
      });
      setMigData(Array.isArray(res?.data) ? res.data : []);
      setMigPage(Number(res?.page || page || 1));
      setMigTotalRecords(Number(res?.totalRecords || 0));
      setMigTotalPages(Number(res?.totalPages || 0));
      setMigOutcome(String(res?.outcome || outcome || 'all'));
    } catch (e) {
      setMigData([]);
      setMigTotalRecords(0);
      setMigTotalPages(0);
      setMigError(e?.data?.error || e?.message || 'Failed to load migrated contributions');
    } finally {
      setMigLoading(false);
    }
  }, [migPageSize]);

  const loadMigrationLogs = React.useCallback(async () => {
    setLogsLoading(true);
    setLogsError('');
    try {
      const scheduleRunId = logsQueueJobId ? `scheduler-job-${logsQueueJobId}` : '';
      const typedRunId = String(logsRunId || '').trim();
      const effectiveRunId = scheduleRunId || typedRunId;
      const params = {
        q: logsQ || undefined,
        runId: effectiveRunId || undefined,
        level: logsLevel,
        limit: Number(logsLimit || 200),
      };
      if (!effectiveRunId) {
        const ft = String(logsSelFinancialType || '').trim();
        if (ft !== '' && ft !== '__none__' && /^-?\d+$/.test(ft)) {
          params.financialTypeId = ft;
        }
        const cp = String(logsSelContributionPage || '').trim();
        if (cp !== '') {
          if (cp === '__none__') {
            params.contributionPageId = 0;
          } else if (/^-?\d+$/.test(cp)) {
            params.contributionPageId = cp;
          }
        }
      }
      const res = await get('/api/manage/civi-wc-product-mapping/migration-logs', params);
      setLogsRows(Array.isArray(res?.data) ? res.data : []);
      setLogsTotal(Number(res?.totalRecords || 0));
    } catch (e) {
      setLogsRows([]);
      setLogsTotal(0);
      setLogsError(e?.data?.error || e?.message || 'Failed to load migration logs');
    } finally {
      setLogsLoading(false);
    }
  }, [
    logsQ,
    logsRunId,
    logsLevel,
    logsLimit,
    logsQueueJobId,
    logsSelFinancialType,
    logsSelContributionPage,
  ]);

  const openMigratedDialog = (row, outcome) => {
    if (!row?.id) return;
    setMigRow(row);
    setMigOutcome(outcome);
    setMigData([]);
    setMigError('');
    setMigPage(1);
    setMigTotalPages(0);
    setMigTotalRecords(0);
    setMigOpen(true);
    loadMigratedDialog(row, outcome, 1);
  };

  const closeMigratedDialog = () => {
    setMigOpen(false);
    setMigRow(null);
    setMigData([]);
    setMigError('');
    setMigPage(1);
    setMigTotalPages(0);
    setMigTotalRecords(0);
  };

  const schedulerActionHandlersRef = React.useRef({ openMap, openContributions, openMigratedDialog });
  schedulerActionHandlersRef.current = { openMap, openContributions, openMigratedDialog };

  React.useEffect(() => {
    const sa = location.state?.schedulerAction;
    if (!sa) {
      schedulerHandledSigRef.current = null;
      return;
    }
    if (loading) return;
    if (!rows?.length) return;
    const mid = Number(sa.mappingId);
    if (!Number.isFinite(mid) || mid <= 0) {
      navigate('.', { replace: true, state: {} });
      return;
    }
    const row = rows.find((x) => Number(x.id) === mid);
    if (!row) {
      navigate('.', { replace: true, state: {} });
      return;
    }
    const sig = JSON.stringify(sa);
    if (schedulerHandledSigRef.current === sig) return;
    schedulerHandledSigRef.current = sig;
    const out =
      sa.type === 'migrated' && ['completed', 'failed', 'all'].includes(String(sa.outcome))
        ? sa.outcome
        : 'all';
    navigate('.', { replace: true, state: {} });
    const { openMap: om, openContributions: oc, openMigratedDialog: omig } = schedulerActionHandlersRef.current;
    if (sa.type === 'migrated') omig(row, out);
    else if (sa.type === 'contrib') oc(row);
    else if (sa.type === 'map') om(row);
  }, [rows, location.state, loading, navigate]);

  const closeMigrationLogsDialog = React.useCallback(() => {
    setLogsOpen(false);
    setLogsSelFinancialType('');
    setLogsSelContributionPage('');
    setLogsQueueJobId('');
    setLogsQueueJobs([]);
    setLogsRunId('');
    setLogsQ('');
  }, []);

  React.useEffect(() => {
    if (!logsOpen) return undefined;
    const validCp = new Set(logsCpOptionsForDialog.map((o) => o.value));
    if (logsSelContributionPage !== '' && !validCp.has(logsSelContributionPage)) {
      setLogsSelContributionPage('');
    }
    return undefined;
  }, [logsOpen, logsCpOptionsForDialog, logsSelContributionPage]);

  React.useEffect(() => {
    if (!logsOpen) return undefined;
    let cancelled = false;
    (async () => {
      setLogsQueueLoading(true);
      try {
        const params = { limit: 200 };
        const ft = String(logsSelFinancialType || '').trim();
        const cp = String(logsSelContributionPage || '').trim();
        if (ft !== '' && ft !== '__none__' && /^-?\d+$/.test(ft)) {
          params.financialTypeId = ft;
        }
        if (cp !== '') {
          if (cp === '__none__') {
            params.contributionPageId = 0;
          } else if (/^-?\d+$/.test(cp)) {
            params.contributionPageId = cp;
          }
        }
        const res = await get('/api/manage/civi-migration-scheduler/queue', params);
        if (!cancelled) {
          setLogsQueueJobs(Array.isArray(res?.rows) ? res.rows : []);
        }
      } catch {
        if (!cancelled) setLogsQueueJobs([]);
      } finally {
        if (!cancelled) setLogsQueueLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logsOpen, logsSelFinancialType, logsSelContributionPage]);

  React.useEffect(() => {
    if (!logsOpen) return undefined;
    loadMigrationLogs();
    return undefined;
  }, [logsOpen, loadMigrationLogs]);

  React.useEffect(() => {
    if (!logsOpen || !logsAutoRefresh) return undefined;
    const id = setInterval(loadMigrationLogs, 15000);
    return () => clearInterval(id);
  }, [logsOpen, logsAutoRefresh, loadMigrationLogs]);

  React.useEffect(() => {
    if (!recordMigRunId) return undefined;
    let cancelled = false;
    let intervalId = null;
    const cids = recordMigRows.map(contributionIdOf).filter((n) => Number.isFinite(n));
    const pollMs = 5000;
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const poll = async () => {
      try {
        const res = await get(`/api/manage/civi-wc-product-mapping/migrate-selected/${encodeURIComponent(recordMigRunId)}`);
        if (cancelled) return;
        setRecordMigError('');
        setRecordMigRunState(String(res?.state || ''));
        setRecordMigSummary(res?.summary || null);
        const statusMap = {};
        const rows = Array.isArray(res?.contributions) ? res.contributions : [];
        rows.forEach((r) => {
          const cid = Number(r?.contribution_id);
          if (Number.isFinite(cid) && cid > 0) statusMap[cid] = String(r?.status || '');
        });
        if (Object.keys(statusMap).length > 0) {
          setRecordMigStatusById((prev) => ({ ...prev, ...statusMap }));
        }
        if (Array.isArray(res?.data)) {
          setRecordMigResult({ data: res.data });
        }
        const terminal = String(res?.state || '') === 'completed' || String(res?.state || '') === 'failed';
        if (terminal) {
          setContribSelectedIds((prev) => prev.filter((id) => !Object.prototype.hasOwnProperty.call(statusMap, id)));
          if (contribRow) await loadMigratedDialog(contribRow, 'all', 1);
          stopPolling();
        }
      } catch (e) {
        if (cancelled) return;
        const msg = e?.data?.error || e?.message || 'Failed to poll migration status';
        const notFound = e?.status === 404 || /not found|expired/i.test(String(msg));
        if (notFound && cids.length > 0 && contribRow?.id) {
          const ok = await reconcileRecordMigrationFromDb(
            cids,
            'Run status is not available on this server (common with multiple Node instances behind a load balancer). Loaded results from the database:',
          );
          if (ok) {
            setRecordMigError('');
            if (contribRow) await loadMigratedDialog(contribRow, 'all', 1);
            stopPolling();
            return;
          }
        }
        setRecordMigError(
          notFound && cids.length > 0
            ? `${msg} Enable sticky sessions for admin API traffic, or check migration logs for this run.`
            : msg,
        );
      }
    };
    poll();
    intervalId = setInterval(poll, pollMs);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [recordMigRunId, recordMigRows, contribRow, loadMigratedDialog, reconcileRecordMigrationFromDb]);

  const migratedCount = (r, key) => {
    const n = Number(r?.[key]);
    return Number.isFinite(n) ? n : 0;
  };

  const renderContributionStatusIcon = (statusRaw) => {
    const st = String(statusRaw || '').trim().toLowerCase();
    if (st === 'processing' || st === 'queued') {
      return <AutorenewIcon fontSize="small" color="info" sx={{ animation: 'spin 1.1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />;
    }
    if (st === 'completed') {
      return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    }
    if (st === 'failed') {
      return <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />;
    }
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  };

  const canProceedRecordMigration = React.useMemo(() => {
    if (recordMigSubmitting) return false;
    if (!recordMigSelectedProduct?.id || recordMigRows.length === 0) return false;
    const queued = Number(recordMigSummary?.queued || 0);
    const processing = Number(recordMigSummary?.processing || 0);
    const failed = Number(recordMigSummary?.failed || 0);
    const inProgress = !!recordMigRunId && (
      String(recordMigRunState || '').trim().toLowerCase() === 'running'
      || queued > 0
      || processing > 0
    );
    if (inProgress) return false;
    const hasSummary = queued + processing + Number(recordMigSummary?.completed || 0) + failed > 0;
    const variationRequiredButMissing = recordMigVariations.length > 0 && String(recordMigVariationId || '').trim() === '';
    if (variationRequiredButMissing) return false;
    if (!hasSummary) return true;
    // After at least one run, allow Proceed only when there is pending/failed work.
    return failed > 0;
  }, [
    recordMigSubmitting,
    recordMigRunId,
    recordMigRunState,
    recordMigSelectedProduct,
    recordMigRows,
    recordMigSummary,
    recordMigVariations,
    recordMigVariationId,
  ]);

  const manualMappingCliWithCd = React.useMemo(() => {
    if (!mappingRow) return '';
    const ft = Number(mappingRow.financial_type_id);
    if (!Number.isFinite(ft) || ft < 0) return '';
    const cpRaw = Number(mappingRow.contribution_page_id);
    const cp = Number.isFinite(cpRaw) && cpRaw >= 0 ? cpRaw : 0;
    const cmd = `node scripts/civi-contribution-migration/index.js --financial-type-id=${ft} --contribution-page-id=${cp}`;
    return `cd /var/www/icct-payments-app/kiosk-backend\n${cmd}`;
  }, [mappingRow]);

  const onCopyMappingCliCommand = React.useCallback(async () => {
    if (!manualMappingCliWithCd) return;
    try {
      await navigator.clipboard.writeText(manualMappingCliWithCd);
      setError('');
      setOk('Copied migration commands.');
    } catch (_) {
      setError('Failed to copy commands. Please copy manually.');
    }
  }, [manualMappingCliWithCd]);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/manage')} aria-label="Back to Manage" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              CiviCRM migration — product mapping
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Map CiviCRM financial types / contribution pages to WooCommerce products (and variations when present).
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/manage/civi-contributions-migrated')}>
            Migrated Contributions
          </Button>
        </Stack>
      </Stack>

      {error && !mapOpen && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {ok && (
        <Typography color="success.main" sx={{ mb: 2 }}>
          {ok}
        </Typography>
      )}

      <Box sx={{ mb: 2, maxWidth: 520 }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          placeholder="Financial type, contribution page, Woo product…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="filter-mig-status-label">Migration status</InputLabel>
          <Select
            labelId="filter-mig-status-label"
            label="Migration status"
            value={filterMigrationStatus}
            onChange={(e) => setFilterMigrationStatus(String(e.target.value || 'all'))}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="in_progress">In progress</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="scheduled">Scheduled (queue)</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-woo-product-label">Woo product</InputLabel>
          <Select
            labelId="filter-woo-product-label"
            label="Woo product"
            value={filterWooProduct}
            onChange={(e) => setFilterWooProduct(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {wooProductFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-ft-label">Financial type</InputLabel>
          <Select
            labelId="filter-ft-label"
            label="Financial type"
            value={filterFinancialType}
            onChange={(e) => setFilterFinancialType(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {financialTypeFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-cp-label">Contribution page</InputLabel>
          <Select
            labelId="filter-cp-label"
            label="Contribution page"
            value={filterContributionPage}
            onChange={(e) => setFilterContributionPage(String(e.target.value || ''))}
          >
            <MenuItem value="">All</MenuItem>
            {contributionPageFilterOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'financial_type' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'financial_type'}
                  direction={sortBy === 'financial_type' ? sortDir : 'asc'}
                  onClick={() => requestSort('financial_type')}
                >
                  Financial type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'contribution_page' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'contribution_page'}
                  direction={sortBy === 'contribution_page' ? sortDir : 'asc'}
                  onClick={() => requestSort('contribution_page')}
                >
                  Contribution page
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'woo_product' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'woo_product'}
                  direction={sortBy === 'woo_product' ? sortDir : 'asc'}
                  onClick={() => requestSort('woo_product')}
                >
                  Woo product
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'woo_variation' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'woo_variation'}
                  direction={sortBy === 'woo_variation' ? sortDir : 'asc'}
                  onClick={() => requestSort('woo_variation')}
                >
                  Woo variation
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'migration_status' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'migration_status'}
                  direction={sortBy === 'migration_status' ? sortDir : 'asc'}
                  onClick={() => requestSort('migration_status')}
                >
                  Migration status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'mapped' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'mapped'}
                  direction={sortBy === 'mapped' ? sortDir : 'asc'}
                  onClick={() => requestSort('mapped')}
                >
                  Mapped
                </TableSortLabel>
              </TableCell>
              <TableCell>Migrated (completed / failed)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8}>Loading…</TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No rows (check table <code>civi_wc_product_mapping</code>).</TableCell>
              </TableRow>
            )}
            {!loading && rows.length > 0 && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>No results for this search.</TableCell>
              </TableRow>
            )}
            {!loading &&
              filteredRows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={
                    isMappingRowSuccessHighlight(r)
                      ? {
                          bgcolor: alpha(theme.palette.success.main, 0.07),
                          '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.11) },
                        }
                      : hasActiveSchedulerQueue(r)
                        ? {
                            bgcolor: alpha(theme.palette.warning.main, 0.07),
                            '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.11) },
                          }
                        : undefined
                  }
                >
                  <TableCell>{civiCellWithId(r.financial_type_name, r.financial_type_id)}</TableCell>
                  <TableCell>{civiCellWithId(r.contribution_page_name, r.contribution_page_id)}</TableCell>
                  <TableCell>{wooProductCell(r)}</TableCell>
                  <TableCell>{wooVariationCell(r)}</TableCell>
                  <TableCell sx={{ maxWidth: 320, verticalAlign: 'top' }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={isMappingRowErrorStatus(r.migration_status) ? { color: 'error.main', fontWeight: 600 } : {}}
                      >
                        {r.migration_status != null && String(r.migration_status).trim() !== '' ? String(r.migration_status) : '—'}
                      </Typography>
                      {isMappingRowErrorStatus(r.migration_status)
                        && r.error_message != null
                        && String(r.error_message).trim() !== '' && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: 'error.main', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}
                        >
                          {String(r.error_message)}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{hasAnyMapping(r) ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      {migratedCount(r, 'migrated_completed_count') > 0 ? (
                        <Link
                          component="button"
                          type="button"
                          onClick={() => openMigratedDialog(r, 'completed')}
                          sx={{ cursor: 'pointer', verticalAlign: 'inherit', fontSize: '0.875rem' }}
                        >
                          {migratedCount(r, 'migrated_completed_count')}
                        </Link>
                      ) : (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {migratedCount(r, 'migrated_completed_count')}
                        </Typography>
                      )}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /
                      </Typography>
                      {migratedCount(r, 'migrated_failed_count') > 0 ? (
                        <Link
                          component="button"
                          type="button"
                          onClick={() => openMigratedDialog(r, 'failed')}
                          sx={{ cursor: 'pointer', verticalAlign: 'inherit', fontSize: '0.875rem' }}
                        >
                          {migratedCount(r, 'migrated_failed_count')}
                        </Link>
                      ) : (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {migratedCount(r, 'migrated_failed_count')}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    <IconButton
                      size="small"
                      aria-label="Row actions"
                      aria-haspopup="true"
                      aria-expanded={Boolean(rowActionsAnchor) && rowActionsRow?.id === r.id}
                      onClick={(e) => {
                        setRowActionsAnchor(e.currentTarget);
                        setRowActionsRow(r);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={rowActionsAnchor}
        open={Boolean(rowActionsAnchor)}
        onClose={() => {
          setRowActionsAnchor(null);
          setRowActionsRow(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const row = rowActionsRow;
            setRowActionsAnchor(null);
            setRowActionsRow(null);
            if (row) openDetails(row);
          }}
        >
          Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = rowActionsRow;
            setRowActionsAnchor(null);
            setRowActionsRow(null);
            if (row) openMap(row);
          }}
        >
          Map &amp; Schedule
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = rowActionsRow;
            setRowActionsAnchor(null);
            setRowActionsRow(null);
            if (row) openContributions(row);
          }}
        >
          Manual Migration
        </MenuItem>
      </Menu>

      <Dialog
        open={mapOpen}
        onClose={closeMap}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minWidth: { md: 980 } } }}
      >
        <DialogTitle>Map to WooCommerce</DialogTitle>
        <DialogContent>
          {mappingRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {isMappingRowErrorStatus(mappingRow.migration_status) && (
                <Alert severity="error">
                  <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
                    {String(mappingRow.migration_status || '').trim() || 'Error'}
                  </Typography>
                  {mappingRow.error_message != null && String(mappingRow.error_message).trim() !== '' && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {String(mappingRow.error_message)}
                    </Typography>
                  )}
                </Alert>
              )}
              <Typography variant="subtitle2" color="text.secondary">
                CiviCRM record (read-only)
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  label="Financial type"
                  value={civiCellWithId(mappingRow.financial_type_name, mappingRow.financial_type_id)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Contribution page"
                  value={civiCellWithId(mappingRow.contribution_page_name, mappingRow.contribution_page_id)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Mapping row ID"
                  value={mappingRow.id != null ? String(mappingRow.id) : ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Stack>

              <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                Amount-range override rules (first match wins)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Precedence: amount range, then contribution year ({CIVI_YEAR_OVERRIDE_MIN}–{CIVI_YEAR_OVERRIDE_MAX}), then default Woo product below. First match wins; no match skips the contribution.
              </Typography>
              <Stack spacing={1}>
                {overrideRules.map((r, idx) => (
                  <Stack key={`ovr-${idx}`} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      label="Min amount"
                      value={r.min_amount}
                      onChange={(e) => setOverrideRuleField(idx, 'min_amount', e.target.value)}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      label="Max amount"
                      value={r.max_amount}
                      onChange={(e) => setOverrideRuleField(idx, 'max_amount', e.target.value)}
                      sx={{ width: 120 }}
                    />
                    <Autocomplete
                      size="small"
                      sx={{ width: 280 }}
                      options={(() => {
                        const base = overrideProductOptions[idx] || [];
                        if (!r.wc_product_id) return base;
                        const has = base.some((o) => String(o?.id) === String(r.wc_product_id));
                        if (has) return base;
                        return [{ id: Number(r.wc_product_id), name: r.wc_product_name || '' }, ...base];
                      })()}
                      getOptionLabel={(o) => (o?.name ? `${o.name} (#${o.id})` : `#${o?.id || ''}`)}
                      value={r.wc_product_id ? { id: Number(r.wc_product_id), name: r.wc_product_name || '' } : null}
                      inputValue={overrideProductInputs[idx] ?? (r.wc_product_name ? `${r.wc_product_name} (#${r.wc_product_id})` : '')}
                      onInputChange={(_, v, reason) => {
                        if (reason === 'reset') return;
                        setOverrideProductInputs((prev) => ({ ...prev, [idx]: v }));
                        loadOverrideProductOptions(idx, v);
                      }}
                      onChange={(_, v) => {
                        setOverrideRuleField(idx, 'wc_product_id', v?.id != null ? String(v.id) : '');
                        setOverrideRuleField(idx, 'wc_product_name', v?.name ? String(v.name) : '');
                        loadOverrideVariations(idx, v?.id || 0, '');
                      }}
                      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                      renderInput={(params) => (
                        <TextField {...params} label="Search product" placeholder="Type at least 2 characters" />
                      )}
                    />
                    {!r.wc_product_id ? (
                      <TextField
                        size="small"
                        label="Variation"
                        value=""
                        InputProps={{ readOnly: true }}
                        placeholder="Select product first"
                        sx={{ width: 240 }}
                      />
                    ) : (overrideVariationOptions[idx] || []).length === 0 ? (
                      <TextField
                        size="small"
                        label="Variation"
                        value="No published variations"
                        InputProps={{ readOnly: true }}
                        sx={{ width: 240 }}
                      />
                    ) : (
                      <FormControl size="small" sx={{ width: 240 }}>
                        <InputLabel id={`override-var-select-label-${idx}`}>Variation (optional)</InputLabel>
                        <Select
                          labelId={`override-var-select-label-${idx}`}
                          label="Variation (optional)"
                          value={String(r.wc_variation_id || '')}
                          onChange={(e) => setOverrideRuleField(idx, 'wc_variation_id', String(e.target.value || ''))}
                          renderValue={(val) => {
                            if (val === '') return 'Product only (no variation)';
                            const v = (overrideVariationOptions[idx] || []).find((x) => String(x.id) === String(val));
                            return v ? variationSelectLabel(v) : String(val);
                          }}
                        >
                          <MenuItem value="">
                            <em>Product only (no variation)</em>
                          </MenuItem>
                          {(overrideVariationOptions[idx] || []).map((v) => (
                            <MenuItem key={v.id} value={String(v.id)}>
                              {variationSelectLabel(v)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button size="small" color="error" onClick={() => removeOverrideRuleRow(idx)}>
                      Remove
                    </Button>
                  </Stack>
                ))}
                <Box>
                  <Button size="small" variant="outlined" onClick={addOverrideRuleRow}>
                    Add override row
                  </Button>
                </Box>
              </Stack>

              <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                Contribution year rules ({CIVI_YEAR_OVERRIDE_MIN}–{CIVI_YEAR_OVERRIDE_MAX}, first match wins)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Used only when no amount-range row matches. Calendar year is taken from the contribution date. Same precedence as above: year rules before default product.
              </Typography>
              <Stack spacing={1}>
                {yearOverrideRules.map((r, idx) => (
                  <Stack key={`year-ovr-${idx}`} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`year-ovr-yr-${idx}`}>Year</InputLabel>
                      <Select
                        labelId={`year-ovr-yr-${idx}`}
                        label="Year"
                        value={r.contribution_year === '' ? '' : String(r.contribution_year)}
                        onChange={(e) => setYearOverrideRuleField(idx, 'contribution_year', String(e.target.value || ''))}
                      >
                        <MenuItem value="">
                          <em>Select year…</em>
                        </MenuItem>
                        {YEAR_OVERRIDE_OPTIONS.map((y) => (
                          <MenuItem key={y} value={String(y)}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Autocomplete
                      size="small"
                      sx={{ width: 280 }}
                      options={(() => {
                        const base = yearOverrideProductOptions[idx] || [];
                        if (!r.wc_product_id) return base;
                        const has = base.some((o) => String(o?.id) === String(r.wc_product_id));
                        if (has) return base;
                        return [{ id: Number(r.wc_product_id), name: r.wc_product_name || '' }, ...base];
                      })()}
                      getOptionLabel={(o) => (o?.name ? `${o.name} (#${o.id})` : `#${o?.id || ''}`)}
                      value={r.wc_product_id ? { id: Number(r.wc_product_id), name: r.wc_product_name || '' } : null}
                      inputValue={
                        yearOverrideProductInputs[idx] ?? (r.wc_product_name ? `${r.wc_product_name} (#${r.wc_product_id})` : '')
                      }
                      onInputChange={(_, v, reason) => {
                        if (reason === 'reset') return;
                        setYearOverrideProductInputs((prev) => ({ ...prev, [idx]: v }));
                        loadYearOverrideProductOptions(idx, v);
                      }}
                      onChange={(_, v) => {
                        setYearOverrideRuleField(idx, 'wc_product_id', v?.id != null ? String(v.id) : '');
                        setYearOverrideRuleField(idx, 'wc_product_name', v?.name ? String(v.name) : '');
                        loadYearOverrideVariations(idx, v?.id || 0, '');
                      }}
                      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                      renderInput={(params) => (
                        <TextField {...params} label="Search product" placeholder="Type at least 2 characters" />
                      )}
                    />
                    {!r.wc_product_id ? (
                      <TextField
                        size="small"
                        label="Variation"
                        value=""
                        InputProps={{ readOnly: true }}
                        placeholder="Select product first"
                        sx={{ width: 240 }}
                      />
                    ) : (yearOverrideVariationOptions[idx] || []).length === 0 ? (
                      <TextField
                        size="small"
                        label="Variation"
                        value="No published variations"
                        InputProps={{ readOnly: true }}
                        sx={{ width: 240 }}
                      />
                    ) : (
                      <FormControl size="small" sx={{ width: 240 }}>
                        <InputLabel id={`year-ovr-var-${idx}`}>Variation (optional)</InputLabel>
                        <Select
                          labelId={`year-ovr-var-${idx}`}
                          label="Variation (optional)"
                          value={String(r.wc_variation_id || '')}
                          onChange={(e) => setYearOverrideRuleField(idx, 'wc_variation_id', String(e.target.value || ''))}
                          renderValue={(val) => {
                            if (val === '') return 'Product only (no variation)';
                            const v = (yearOverrideVariationOptions[idx] || []).find((x) => String(x.id) === String(val));
                            return v ? variationSelectLabel(v) : String(val);
                          }}
                        >
                          <MenuItem value="">
                            <em>Product only (no variation)</em>
                          </MenuItem>
                          {(yearOverrideVariationOptions[idx] || []).map((v) => (
                            <MenuItem key={v.id} value={String(v.id)}>
                              {variationSelectLabel(v)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button size="small" color="error" onClick={() => removeYearOverrideRuleRow(idx)}>
                      Remove
                    </Button>
                  </Stack>
                ))}
                <Box>
                  <Button size="small" variant="outlined" onClick={addYearOverrideRuleRow}>
                    Add year rule row
                  </Button>
                </Box>
              </Stack>

              <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                WooCommerce default mapping
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pb: 0.5 }}>
                Optional fallback after amount and year rules. If nothing matches, the contribution is skipped during migration.
              </Typography>
              <Autocomplete
                options={productAutocompleteOptions}
                getOptionLabel={(o) => (o?.name ? `${o.name} (#${o.id})` : '')}
                value={selectedProduct}
                onChange={(_, v) => {
                  setSelectedProduct(v);
                  if (!v) setVariationId('');
                }}
                inputValue={productInput}
                onInputChange={(_, v) => setProductInput(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Search product name (optional)" placeholder="Type at least 2 characters" />
                )}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
              />

              {selectedProduct?.id != null && (
                <>
                  {variations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No published variations for this product — only the product ID will be stored.
                    </Typography>
                  ) : (
                    <>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Variations are listed in order: lowest price first, then by variation ID. Amount is from the synced Woo price.
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel id="var-select-label">Variation (required)</InputLabel>
                      <Select
                        labelId="var-select-label"
                        label="Variation (required)"
                        value={variationId}
                        onChange={(e) => setVariationId(e.target.value)}
                        renderValue={(val) => {
                          if (val === '') return 'Select variation…';
                          const v = variations.find((x) => String(x.id) === String(val));
                          return v ? variationSelectLabel(v) : val;
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select variation…</em>
                        </MenuItem>
                        {variations.map((v, idx) => (
                          <MenuItem key={v.id} value={String(v.id)}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25 }}>
                              <Typography variant="body2" component="span">
                                {idx + 1}. {variationSelectLabel(v)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Variation ID: {v.id}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    </>
                  )}
                </>
              )}

              <TextField
                size="small"
                label="Equivalent command-line run"
                value={manualMappingCliWithCd}
                fullWidth
                multiline
                minRows={2}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'error.main' },
                  endAdornment: (
                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                      <Tooltip title="Copy both lines">
                        <span>
                          <IconButton
                            size="small"
                            onClick={onCopyMappingCliCommand}
                            disabled={!manualMappingCliWithCd}
                            aria-label="Copy migration commands"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText="Copy both lines to run on the VM, or use Schedule / Map & Schedule to queue runs (FIFO, one pass per item; 5 min gap after each item completes or fails before the next starts)."
              />

              {error && mapOpen && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              {ok && mapOpen && (
                <Typography color="success.main" variant="body2">
                  {ok}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={closeMap}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={onSaveMapping}
            disabled={
              saving
              || (Boolean(selectedProduct?.id) && variations.length > 0 && variationId === '')
            }
          >
            {saving ? 'Saving…' : 'Save mapping'}
          </Button>
          <Button
            variant="contained"
            onClick={onMapAndSchedule}
            disabled={
              saving
              || (() => {
                const mig = String(mappingRow?.migration_status || '').trim().toLowerCase();
                if (mig === 'completed' || mig === 'success') return true;
                return !dialogHasMappableDraft({
                  selectedProduct,
                  variationId,
                  variations,
                  overrideRules,
                  yearOverrideRules,
                });
              })()
              || (Boolean(selectedProduct?.id) && variations.length > 0 && variationId === '')
            }
          >
            {saving ? 'Saving…' : 'Map & Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>Mapping record details</DialogTitle>
        <DialogContent>
          {!detailsRow ? (
            <Typography variant="body2" color="text.secondary">No row selected.</Typography>
          ) : (
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '35%' }}>Column</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(detailsRow).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>{k}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {v == null ? 'NULL' : String(v)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contribOpen}
        onClose={closeContributions}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: '78vh', maxHeight: '90vh' } }}
      >
        <DialogTitle>
          Contribution records
          {contribRow ? ` — ${civiCellWithId(contribRow.financial_type_name, contribRow.financial_type_id)} / ${civiCellWithId(contribRow.contribution_page_name, contribRow.contribution_page_id)}` : ''}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 1 }}>
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.secondary">
                {contribLoading
                  ? 'Loading records...'
                  : `Total records: ${contribTotalRecords} • Page ${contribPage}${contribTotalPages > 0 ? ` of ${contribTotalPages}` : ''}`}
              </Typography>
              {recordMigSummary && (
                <Typography variant="caption" color="text.secondary">
                  Current migration summary: queued {Number(recordMigSummary?.queued || 0)} • processing {Number(recordMigSummary?.processing || 0)} • completed {Number(recordMigSummary?.completed || 0)} • failed {Number(recordMigSummary?.failed || 0)}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={contribLoading || contribSelectedIds.length === 0}
                onClick={openGroupMigration}
              >
                Group migrate ({contribSelectedIds.length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={contribLoading || contribPage <= 1}
                onClick={() => loadContributions(contribRow, Math.max(1, contribPage - 1))}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={contribLoading || (contribTotalPages > 0 && contribPage >= contribTotalPages)}
                onClick={() => loadContributions(contribRow, contribPage + 1)}
              >
                Next
              </Button>
            </Stack>
          </Stack>
          {contribError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {contribError}
            </Typography>
          )}
          {!contribLoading && contribTotalRecords === 0 && !contribError && contribRow?.id && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Button variant="outlined" size="small" disabled={contribMarking} onClick={onMarkMigrationCompleted}>
                {contribMarking ? 'Updating…' : 'Mark migration as completed'}
              </Button>
            </Stack>
          )}
          {contribMarkCompletedMsg && (
            <Typography color="success.main" variant="body2" sx={{ mb: 1 }}>
              {contribMarkCompletedMsg}
            </Typography>
          )}
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 360 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1800 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ minWidth: 54 }}>
                    <Checkbox
                      size="small"
                      checked={contributionSelectionOnPage.allChecked}
                      indeterminate={contributionSelectionOnPage.indeterminate}
                      onChange={(e) => toggleSelectAllOnPage(!!e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Migrate</TableCell>
                  <TableCell sx={{ minWidth: 86 }}>Status</TableCell>
                  <TableCell>Contribution ID</TableCell>
                  <TableCell>Receive date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Net</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>Financial type</TableCell>
                  <TableCell>Contribution page</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Display name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Payment method</TableCell>
                  <TableCell>Check #</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Zip</TableCell>
                  <TableCell>Country</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!contribLoading && contribData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={21}>No contribution records found.</TableCell>
                  </TableRow>
                )}
                {contribData.map((c, idx) => (
                  <TableRow
                    key={`${c.contributionId || c.id || 'row'}-${idx}`}
                    hover
                    sx={(() => {
                      const st = contributionRunStatus(contributionIdOf(c));
                      if (st === 'completed') return { bgcolor: 'rgba(46, 125, 50, 0.08)' };
                      if (st === 'failed') return { bgcolor: 'rgba(211, 47, 47, 0.08)' };
                      return undefined;
                    })()}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={(() => {
                          const cid = contributionIdOf(c);
                          return Number.isFinite(cid) ? contribSelectedIds.includes(cid) : false;
                        })()}
                        onChange={(e) => {
                          const cid = contributionIdOf(c);
                          if (!Number.isFinite(cid)) return;
                          toggleContributionSelection(cid, !!e.target.checked);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Migrate this record">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openSingleRecordMigration(c)}
                            disabled={!Number.isFinite(contributionIdOf(c))}
                          >
                            <PlayCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{renderContributionStatusIcon(contributionRunStatus(contributionIdOf(c)))}</TableCell>
                    <TableCell>{String(c.contributionId || c.id || '—')}</TableCell>
                    <TableCell>{fmtDateTime(c.receiveDate || c.receive_date || c.contributionDate || c.contribution_date)}</TableCell>
                    <TableCell>{fmtMoney(c.contributionAmount || c.total_amount || c.amount)}</TableCell>
                    <TableCell>{fmtMoney(c.netAmount || c.net_amount)}</TableCell>
                    <TableCell>{fmtMoney(c.feeAmount || c.fee_amount)}</TableCell>
                    <TableCell>{String(c.financialTypeName || c.financial_type_name || '—')}</TableCell>
                    <TableCell>{String(c.contributionPageName || c.contribution_page_name || '—')}</TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(() => {
                        const src = c.source ?? c.Source;
                        return src != null && String(src).trim() !== '' ? String(src) : '—';
                      })()}
                    </TableCell>
                    <TableCell>{contribDisplayName(c)}</TableCell>
                    <TableCell>{String(c.customerEmail || c.email || '—')}</TableCell>
                    <TableCell>{String(c.userId || c.user_id || '—')}</TableCell>
                    <TableCell>{String(c.paymentMethod || c.payment_method || '—')}</TableCell>
                    <TableCell>{String(c.checkNumber || c.check_number || '—')}</TableCell>
                    <TableCell>{String(c.streetAddress || c.address1 || c.address_1 || '—')}</TableCell>
                    <TableCell>{String(c.city || '—')}</TableCell>
                    <TableCell>{String(c.stateProvince || c.state || '—')}</TableCell>
                    <TableCell>{String(c.postalCode || c.postcode || '—')}</TableCell>
                    <TableCell>{String(c.country || '—')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContributions}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={migOpen}
        onClose={closeMigratedDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh', maxHeight: '92vh' } }}
      >
        <DialogTitle component="div">
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Migrated contributions
          </Typography>
          {migRow && (
            <Stack spacing={0.75} sx={{ mt: 1.25, pr: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {civiCellWithId(migRow.financial_type_name, migRow.financial_type_id)}
                {' · '}
                {civiCellWithId(migRow.contribution_page_name, migRow.contribution_page_id)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Woo product: </Box>
                {wooProductCell(migRow)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Variation: </Box>
                {wooVariationCell(migRow)}
              </Typography>
            </Stack>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1, mt: 0.5 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="mig-outcome-label">Show</InputLabel>
              <Select
                labelId="mig-outcome-label"
                label="Show"
                value={migOutcome}
                onChange={(e) => {
                  const v = e.target.value;
                  setMigOutcome(v);
                  setMigPage(1);
                  if (migRow) loadMigratedDialog(migRow, v, 1);
                }}
              >
                <MenuItem value="completed">Completed only</MenuItem>
                <MenuItem value="failed">Failed only</MenuItem>
                <MenuItem value="all">All migrated rows</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {migLoading
                ? 'Loading…'
                : `Total: ${migTotalRecords} • Page ${migPage}${migTotalPages > 0 ? ` of ${migTotalPages}` : ''}`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={migLoading || migPage <= 1}
                onClick={() => loadMigratedDialog(migRow, migOutcome, Math.max(1, migPage - 1))}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={migLoading || (migTotalPages > 0 && migPage >= migTotalPages)}
                onClick={() => loadMigratedDialog(migRow, migOutcome, migPage + 1)}
              >
                Next
              </Button>
            </Stack>
          </Stack>
          {migError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {migError}
            </Typography>
          )}
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'auto', flex: 1, minHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Contribution ID</TableCell>
                  <TableCell>Woo order</TableCell>
                  <TableCell>Migration</TableCell>
                  <TableCell>Civi cancel</TableCell>
                  <TableCell>Order status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Contribution date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!migLoading && migData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>No rows for this filter.</TableCell>
                  </TableRow>
                )}
                {migData.map((row) => (
                  <TableRow key={row.id != null ? `mig-${row.id}` : `c-${row.contribution_id}`} hover>
                    <TableCell>{row.contribution_id != null ? String(row.contribution_id) : '—'}</TableCell>
                    <TableCell>{row.wc_order_id != null ? String(row.wc_order_id) : '—'}</TableCell>
                    <TableCell>{String(row.migration_status || '—')}</TableCell>
                    <TableCell>{String(row.civi_cancel_status || '—')}</TableCell>
                    <TableCell>{String(row.order_status || '—')}</TableCell>
                    <TableCell align="right">{fmtMoney(row.contribution_amount)}</TableCell>
                    <TableCell>{fmtDateTime(row.contribution_date)}</TableCell>
                    <TableCell>{String(row.customer_display_name || '—')}</TableCell>
                    <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {String(row.customer_email || '—')}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {row.source != null && String(row.source).trim() !== '' ? String(row.source) : '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {row.migration_error != null && String(row.migration_error).trim() !== '' ? String(row.migration_error) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMigratedDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recordMigOpen}
        onClose={recordMigSubmitting ? undefined : closeRecordMigrationDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minWidth: { md: 1120 } } }}
      >
        <DialogTitle>
          {recordMigRows.length > 1 ? `Group migrate ${recordMigRows.length} contributions` : 'Migrate contribution record'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              This runs the same migration flow per selected contribution and does not update the mapping table.
            </Typography>
            {recordMigRunId && (
              <Typography variant="caption" color="text.secondary">
                Live status updates every few seconds for this run.
              </Typography>
            )}
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 240 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Contribution ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Receive date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Display name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Payment method</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recordMigRows.map((c, idx) => (
                    <TableRow
                      key={`mig-row-${contributionIdOf(c) || idx}`}
                      sx={(() => {
                        const st = contributionRunStatus(contributionIdOf(c));
                        if (st === 'completed') return { bgcolor: 'rgba(46, 125, 50, 0.08)' };
                        if (st === 'failed') return { bgcolor: 'rgba(211, 47, 47, 0.08)' };
                        return undefined;
                      })()}
                    >
                      <TableCell>{String(contributionIdOf(c) || '—')}</TableCell>
                      <TableCell>{renderContributionStatusIcon(contributionRunStatus(contributionIdOf(c)))}</TableCell>
                      <TableCell>{fmtDateTime(c.receiveDate || c.receive_date || c.contributionDate || c.contribution_date)}</TableCell>
                      <TableCell align="right">{fmtMoney(c.contributionAmount || c.total_amount || c.amount)}</TableCell>
                      <TableCell>{contribDisplayName(c)}</TableCell>
                      <TableCell>{String(c.customerEmail || c.email || '—')}</TableCell>
                      <TableCell>{String(c.userId || c.user_id || '—')}</TableCell>
                      <TableCell>{String(c.paymentMethod || c.payment_method || '—')}</TableCell>
                      <TableCell>{String(c.source || c.Source || '—')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 0.5 }}>
              WooCommerce mapping for this migration run
            </Typography>
            <Autocomplete
              options={(() => {
                if (!recordMigSelectedProduct?.id) return recordMigProductOptions;
                const has = recordMigProductOptions.some((o) => Number(o?.id) === Number(recordMigSelectedProduct.id));
                return has ? recordMigProductOptions : [recordMigSelectedProduct, ...recordMigProductOptions];
              })()}
              getOptionLabel={(o) => (o?.name ? `${o.name} (#${o.id})` : '')}
              value={recordMigSelectedProduct}
              onChange={(_, v) => setRecordMigSelectedProduct(v)}
              inputValue={recordMigProductInput}
              onInputChange={(_, v) => setRecordMigProductInput(v)}
              renderInput={(params) => (
                <TextField {...params} label="Search product name" placeholder="Type at least 2 characters" />
              )}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            />
            {recordMigSelectedProduct?.id != null && (
              <>
                {recordMigVariations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No published variations for this product — migration will use product ID only.
                  </Typography>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="record-mig-var-select-label">Variation (required)</InputLabel>
                    <Select
                      labelId="record-mig-var-select-label"
                      label="Variation (required)"
                      value={recordMigVariationId}
                      onChange={(e) => setRecordMigVariationId(e.target.value)}
                      renderValue={(val) => {
                        if (val === '') return 'Select variation…';
                        const v = recordMigVariations.find((x) => String(x.id) === String(val));
                        return v ? variationSelectLabelWithId(v) : val;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select variation…</em>
                      </MenuItem>
                      {recordMigVariations.map((v, idx) => (
                        <MenuItem key={v.id} value={String(v.id)}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25 }}>
                            <Typography variant="body2" component="span">
                              {idx + 1}. {variationSelectLabelWithId(v)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {recordMigError && (
              <Typography color="error" variant="body2">
                {recordMigError}
              </Typography>
            )}
            {recordMigNotice && (
              <Typography color="warning.main" variant="body2">
                {recordMigNotice}
              </Typography>
            )}
            {recordMigSummary && (
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                Summary: queued {Number(recordMigSummary?.queued || 0)}, processing {Number(recordMigSummary?.processing || 0)}, completed {Number(recordMigSummary?.completed || 0)}, failed {Number(recordMigSummary?.failed || 0)}
              </Typography>
            )}
            {recordMigResult?.data?.length > 0 && (
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Contribution ID</TableCell>
                      <TableCell>Woo order</TableCell>
                      <TableCell>Migration</TableCell>
                      <TableCell>Civi cancel</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Variation</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recordMigResult.data.map((row) => (
                      <TableRow key={`record-mig-result-${row.contribution_id}`}>
                        <TableCell>{String(row.contribution_id || '—')}</TableCell>
                        <TableCell>{String(row.wc_order_id || '—')}</TableCell>
                        <TableCell>{String(row.migration_status || '—')}</TableCell>
                        <TableCell>{String(row.civi_cancel_status || '—')}</TableCell>
                        <TableCell>{row.wc_product_name ? `${row.wc_product_name} (#${row.wc_product_id})` : String(row.wc_product_id || '—')}</TableCell>
                        <TableCell>{row.wc_variation_name ? `${row.wc_variation_name} (#${row.wc_variation_id})` : String(row.wc_variation_id || '—')}</TableCell>
                        <TableCell>{row.migration_error ? String(row.migration_error) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeRecordMigrationDialog}
            disabled={
              recordMigSubmitting
              || (
                !!recordMigRunId
                && (
                  String(recordMigRunState || '').trim().toLowerCase() === 'running'
                  || Number(recordMigSummary?.queued || 0) > 0
                  || Number(recordMigSummary?.processing || 0) > 0
                )
              )
            }
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={onProceedRecordMigration}
            disabled={!canProceedRecordMigration}
          >
            {recordMigSubmitting || recordMigRunState === 'running' ? 'Processing…' : 'Proceed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logsOpen}
        onClose={closeMigrationLogsDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minWidth: { md: 1120 }, minHeight: '78vh' } }}
      >
        <DialogTitle>Migration Logs</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1, mb: 1 }} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="map-logs-ft-label">Financial type</InputLabel>
              <Select
                labelId="map-logs-ft-label"
                label="Financial type"
                value={logsSelFinancialType}
                onChange={(e) => {
                  setLogsSelFinancialType(String(e.target.value));
                  setLogsQueueJobId('');
                }}
              >
                <MenuItem value="">Any</MenuItem>
                {financialTypeFilterOptions.map((o) => (
                  <MenuItem key={`log-ft-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="map-logs-cp-label">Contribution page</InputLabel>
              <Select
                labelId="map-logs-cp-label"
                label="Contribution page"
                value={logsSelContributionPage}
                onChange={(e) => {
                  setLogsSelContributionPage(String(e.target.value));
                  setLogsQueueJobId('');
                }}
              >
                <MenuItem value="">Any</MenuItem>
                {logsCpOptionsForDialog.map((o) => (
                  <MenuItem key={`log-cp-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel id="map-logs-queue-job-label">Queue job</InputLabel>
              <Select
                labelId="map-logs-queue-job-label"
                label="Queue job"
                value={logsQueueJobId}
                onChange={(e) => {
                  const v = String(e.target.value || '');
                  setLogsQueueJobId(v);
                  if (v) {
                    setLogsRunId(`scheduler-job-${v}`);
                  } else {
                    setLogsRunId('');
                  }
                }}
              >
                <MenuItem value="">All (type &amp; page only)</MenuItem>
                {logsQueueJobId
                  && !logsQueueJobs.some((j) => String(j.id) === String(logsQueueJobId)) && (
                  <MenuItem value={logsQueueJobId}>
                    #{logsQueueJobId} (reload list…)
                  </MenuItem>
                )}
                {logsQueueJobs.map((job) => (
                  <MenuItem key={`log-qj-${job.id}`} value={String(job.id)}>
                    {formatSchedulerQueueJobMenuLabel(job)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            Queue jobs load for the selected financial type and contribution page (oldest first when filtered). Choose a job to set run id{' '}
            <code>scheduler-job-#</code>, or All to show log lines matching the type/page without a run id filter.
            {logsQueueLoading ? ' Updating queue…' : ''}
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 0, mb: 1 }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Search logs"
              placeholder="message text, contribution_id, error..."
              value={logsQ}
              onChange={(e) => setLogsQ(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size="small"
              label="Run ID"
              placeholder="optional exact run id"
              value={logsRunId}
              onChange={(e) => {
                setLogsRunId(e.target.value);
                setLogsQueueJobId('');
              }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="map-logs-level-label">Level</InputLabel>
              <Select
                labelId="map-logs-level-label"
                label="Level"
                value={logsLevel}
                onChange={(e) => setLogsLevel(String(e.target.value || 'all'))}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="map-logs-rows-label">Rows</InputLabel>
              <Select
                labelId="map-logs-rows-label"
                label="Rows"
                value={logsLimit}
                onChange={(e) => setLogsLimit(String(e.target.value || '200'))}
              >
                <MenuItem value="100">100</MenuItem>
                <MenuItem value="200">200</MenuItem>
                <MenuItem value="500">500</MenuItem>
                <MenuItem value="1000">1000</MenuItem>
              </Select>
            </FormControl>
            <Button variant={logsAutoRefresh ? 'contained' : 'outlined'} onClick={() => setLogsAutoRefresh((v) => !v)}>
              Auto-refresh: {logsAutoRefresh ? 'On' : 'Off'}
            </Button>
            <Button variant="outlined" onClick={loadMigrationLogs} disabled={logsLoading}>
              {logsLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Matched rows: {logsTotal}
          </Typography>
          {logsError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {logsError}
            </Typography>
          )}
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'auto', flex: 1, minHeight: 320 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1040 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 210 }}>Timestamp</TableCell>
                  <TableCell sx={{ width: 90 }}>Level</TableCell>
                  <TableCell sx={{ width: 310 }}>Run ID</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!logsLoading && logsRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>No logs found for current filters.</TableCell>
                  </TableRow>
                )}
                {logsRows.map((r) => (
                  <TableRow key={`dlg-log-${r.id}-${r.ts}`} hover>
                    <TableCell>{fmtDateTime(r.ts)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: String(r.level || '').toLowerCase() === 'error' ? 'error.main' : 'primary.main' }}
                      >
                        {String(r.level || 'INFO')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8rem' }}>
                      {r.runId ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => {
                            setInlineRunLogId(String(r.runId));
                            setInlineRunLogQ('');
                            setInlineRunLogOpen(true);
                          }}
                          sx={{ cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}
                        >
                          {String(r.runId)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {String(r.message || '')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMigrationLogsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <MigrationRunLogDialog
        open={inlineRunLogOpen}
        onClose={() => setInlineRunLogOpen(false)}
        initialRunId={inlineRunLogId}
        initialSearchQ={inlineRunLogQ}
      />
    </PageLayout>
  );
}
